import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Phone, Mail, Megaphone, Users2, MapPin, Trash2, Upload, Download, Contact, ArrowRight, ShieldOff, Search, MessageCircle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ExportDialog, DATE_FIELDS_BASE, LEAD_EXPORT_FIELDS } from "@/components/ExportDialog";
import { useExports } from "@/context/ExportsContext";
import { Progress } from "@/components/ui/progress";
import { runChunkedImport } from "@/lib/chunkedImport";

const EMPTY = { nome: "", tipo: "telefonica", descrizione: "", assign_type: "sede", sede_id: "none", collaboratori_ids: [], email_subject: "", email_body: "" };

const LEAD_FIELDS = [
  { key: "codice_fiscale", label: "Codice Fiscale (univoco) *" }, { key: "nome", label: "Nome" },
  { key: "cognome", label: "Cognome" }, { key: "cellulare", label: "Cellulare" },
  { key: "telefono", label: "Telefono fisso" }, { key: "email", label: "E-Mail" },
  { key: "original_id", label: "ID origine" }, { key: "datore_lavoro", label: "Datore di Lavoro" },
  { key: "residenza", label: "Residenza" }, { key: "professione", label: "Professione" },
  { key: "data_nascita", label: "Data di nascita" }, { key: "indirizzo", label: "Indirizzo" },
  { key: "provincia", label: "Provincia" }, { key: "cap", label: "Cap" },
  { key: "importo_richiesto", label: "Importo Richiesto" }, { key: "bene_finanziato", label: "Bene Finanziato" },
  { key: "rata", label: "Rata" }, { key: "durata", label: "Durata (mesi)" }, { key: "cluster", label: "Cluster" }, { key: "azienda", label: "Azienda" },
  { key: "convenzionato", label: "Convenzionato" }, { key: "sede_label", label: "Sede (file)" }, { key: "note", label: "Note" },
];

function guess(field, cols) {
  const n = (s) => s.toLowerCase().replace(/[^a-z]/g, "");
  const alias = { codice_fiscale: "codicefiscale", telefono: "telefonofisso", original_id: "id", data_nascita: "datadinascita", sede_label: "sede", durata: "duratamesi" };
  const target = n(alias[field] || field);
  const norm = cols.map((c) => ({ c, nc: n(c) }));
  return (norm.find((x) => x.nc === target) || norm.find((x) => x.nc.includes(target) || target.includes(x.nc)) || {}).c || "none";
}

export default function Marketing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [sedi, setSedi] = useState([]);
  const [collabs, setCollabs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [leadCount, setLeadCount] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [search, setSearch] = useState("");
  const [showScadute, setShowScadute] = useState(false);

  const loadLeadCount = useCallback(() => { api.get("/leads/count").then((r) => setLeadCount(r.data.total)).catch(() => {}); }, []);

  const { startExport } = useExports();
  const onExport = (params) => startExport("leads_export", params, "Lead");

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return; setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/leads/import-preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      const auto = {}; LEAD_FIELDS.forEach((f) => { const g = guess(f.key, data.columns); if (g !== "none") auto[f.key] = g; });
      setMapping(auto);
    } catch { toast.error("Errore lettura file"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const commitImport = async () => {
    setBusy(true); setProgress({ pct: 0, done: 0, total: preview.rows.length });
    try {
      const res = await runChunkedImport({
        rows: preview.rows, chunkSize: 1000,
        postChunk: (chunk, batchId, offset) => api.post("/leads/import-commit", { mapping, rows: chunk, batch_id: batchId, offset }).then((r) => r.data),
        onProgress: (pct, done, total) => setProgress({ pct, done, total }),
      });
      if (res.failed) { toast.error(`Importazione interrotta dopo ${res.processed}/${preview.rows.length} righe (${res.created} creati). Riprova per completare.`); return; }
      if (res.error_count) toast.warning(`${res.created} nuovi · ${res.updated} aggiornati · ${res.error_count} errori (vedi Registro Import)`);
      else toast.success(`${res.created} nuovi · ${res.updated} aggiornati · ${res.skipped_missing} incompleti`);
      setImportOpen(false); setPreview(null); setMapping({}); loadLeadCount();
    } catch { toast.error("Errore importazione"); } finally { setBusy(false); setProgress(null); }
  };

  const load = useCallback(() => {
    const params = {};
    if (search) params.q = search;
    if (showScadute) params.include_scadute = true;
    api.get("/campaigns", { params }).then((r) => setCampaigns(r.data));
  }, [search, showScadute]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    api.get("/sedi").then((r) => setSedi(r.data));
    api.get("/users/internal").then((r) => setCollabs(r.data));
    loadLeadCount();
  }, [loadLeadCount]);

  const create = async () => {
    if (!form.nome.trim()) { toast.error("Inserisci il nome campagna"); return; }
    const payload = { ...form, sede_id: form.assign_type === "sede" && form.sede_id !== "none" ? form.sede_id : null,
      collaboratori_ids: form.assign_type === "collaboratori" ? form.collaboratori_ids : [] };
    try { await api.post("/campaigns", payload); toast.success("Campagna creata"); setOpen(false); setForm(EMPTY); load(); }
    catch (e) { toast.error("Errore"); }
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Eliminare la campagna? I lead NON verranno eliminati: resteranno disponibili nella Rubrica Lead.")) return;
    await api.delete(`/campaigns/${id}`); load(); toast.success("Campagna eliminata · lead conservati in Rubrica");
  };

  const toggleCollab = (id) => setForm((f) => ({ ...f, collaboratori_ids: f.collaboratori_ids.includes(id) ? f.collaboratori_ids.filter((x) => x !== id) : [...f.collaboratori_ids, id] }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Marketing</h1>
          <p className="text-sm text-muted-foreground mt-1">{campaigns.length} campagne · telefoniche, email e WhatsApp</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="rubrica-lead-btn" onClick={() => navigate("/marketing/rubrica")}><Contact className="h-4 w-4 mr-2" />Rubrica Lead</Button>
          <Button variant="outline" data-testid="lead-import-btn" onClick={() => setImportOpen(true)}><Upload className="h-4 w-4 mr-2" />Importa Lead</Button>
          {user?.role === "admin" && <Button variant="outline" data-testid="lead-export-btn" onClick={() => setExportOpen(true)}><Download className="h-4 w-4 mr-2" />Esporta Lead</Button>}
          <ExportDialog open={exportOpen} onOpenChange={setExportOpen} title="Esporta Lead" fields={LEAD_EXPORT_FIELDS} dateFields={DATE_FIELDS_BASE} onConfirm={onExport} />
          <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) { setPreview(null); setMapping({}); } }}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Importa Lead in Rubrica (anteprima)</DialogTitle></DialogHeader>
              {!preview ? (
                <div className="py-6">
                  <Button asChild variant="outline" disabled={busy}>
                    <label className="cursor-pointer" data-testid="rubrica-file-label"><Upload className="h-4 w-4 mr-2" />{busy ? "Lettura…" : "Seleziona file"}<input type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="rubrica-file-input" onChange={onFile} /></label>
                  </Button>
                  <p className="text-xs text-muted-foreground mt-3">Il Codice Fiscale è usato come campo univoco: i lead esistenti vengono aggiornati.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground" data-testid="rubrica-preview-summary">{preview.total} righe rilevate · {preview.columns.length} colonne. Controlla la corrispondenza dei campi prima di importare.</p>
                  <div className="grid sm:grid-cols-2 gap-2 max-h-52 overflow-y-auto pr-1">
                    {LEAD_FIELDS.map((f) => (
                      <div key={f.key} className="flex items-center gap-2">
                        <span className="text-xs w-1/2 truncate" title={f.label}>{f.label}</span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                        <Select value={mapping[f.key] || "none"} onValueChange={(v) => setMapping((m) => ({ ...m, [f.key]: v === "none" ? undefined : v }))}>
                          <SelectTrigger className="flex-1 h-8" data-testid={`rubrica-map-${f.key}`}><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="none">—</SelectItem>{preview.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                  <div className="overflow-x-auto border border-border rounded-md max-h-52">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50"><tr>{preview.columns.slice(0, 8).map((c) => <th key={c} className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">{c}</th>)}</tr></thead>
                      <tbody>{preview.rows.slice(0, 8).map((r, i) => <tr key={i} data-testid={`rubrica-import-row-${i}`} className="border-t border-border">{preview.columns.slice(0, 8).map((c) => <td key={c} className="px-2 py-1.5 whitespace-nowrap max-w-[160px] truncate">{String(r[c])}</td>)}</tr>)}</tbody>
                    </table>
                  </div>
                </div>
              )}
              {progress && (
                <div className="space-y-2 pt-2" data-testid="rubrica-import-progress">
                  <div className="flex justify-between text-sm"><span>Importazione…</span><span className="tabular-nums" data-testid="rubrica-import-pct">{progress.pct}% · {progress.done}/{progress.total}</span></div>
                  <Progress value={progress.pct} />
                </div>
              )}
              <DialogFooter>
                {preview && <Button variant="outline" disabled={busy} onClick={() => { setPreview(null); setMapping({}); }}>Reset</Button>}
                {preview && <Button data-testid="rubrica-import-confirm" onClick={commitImport} disabled={busy}>Importa {preview.total} righe</Button>}
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-campaign-btn"><Plus className="h-4 w-4 mr-2" /> Nuova Campagna</Button></DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nuova Campagna</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5"><Label>Nome campagna *</Label><Input data-testid="campaign-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Tipo</Label>
                  <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                    <SelectTrigger data-testid="campaign-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="telefonica">Telefonica</SelectItem><SelectItem value="email">Email</SelectItem><SelectItem value="whatsapp">WhatsApp</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Assegna a</Label>
                  <Select value={form.assign_type} onValueChange={(v) => setForm({ ...form, assign_type: v })}>
                    <SelectTrigger data-testid="campaign-assign-type"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="sede">Sede</SelectItem><SelectItem value="collaboratori">Collaboratori</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              {form.assign_type === "sede" ? (
                <div className="space-y-1.5"><Label>Sede</Label>
                  <Select value={form.sede_id} onValueChange={(v) => setForm({ ...form, sede_id: v })}>
                    <SelectTrigger data-testid="campaign-sede"><SelectValue placeholder="Seleziona sede" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Nessuna</SelectItem>{sedi.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-1.5"><Label>Collaboratori</Label>
                  <div className="max-h-32 overflow-y-auto rounded-md border border-border p-2 space-y-1">
                    {collabs.map((c) => (
                      <label key={c.id} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-accent cursor-pointer text-sm" data-testid={`campaign-collab-${c.id}`}>
                        <input type="checkbox" checked={form.collaboratori_ids.includes(c.id)} onChange={() => toggleCollab(c.id)} className="accent-primary" />{c.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-1.5"><Label>Descrizione</Label><Textarea value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} /></div>
              {form.tipo === "email" && (
                <>
                  <div className="space-y-1.5"><Label>Oggetto email</Label><Input data-testid="campaign-email-subject" value={form.email_subject} onChange={(e) => setForm({ ...form, email_subject: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Corpo email (inviata ai lead)</Label><Textarea data-testid="campaign-email-body" value={form.email_body} onChange={(e) => setForm({ ...form, email_body: e.target.value })} className="min-h-[100px]" /></div>
                </>
              )}
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button data-testid="campaign-submit" onClick={create}>Crea</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      {leadCount !== null && <p className="text-xs text-muted-foreground -mt-3">Rubrica lead: {leadCount} contatti totali</p>}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="campaign-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca campagna per nome…" className="pl-9" />
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none" data-testid="show-scadute-toggle">
          <Checkbox checked={showScadute} onCheckedChange={(v) => setShowScadute(!!v)} data-testid="show-scadute-checkbox" />
          Visualizza anche scadute
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {campaigns.map((c) => (
          <Card key={c.id} data-testid={`campaign-card-${c.id}`} onClick={() => navigate(`/marketing/${c.id}`)} className="p-5 cursor-pointer hover:border-primary transition-colors">
            <div className="flex items-start gap-3">
              <div className={`flex h-11 w-11 items-center justify-center rounded-lg shrink-0 ${c.tipo === "email" ? "bg-indigo-500/10 text-indigo-500" : c.tipo === "whatsapp" ? "bg-emerald-500/10 text-emerald-600" : "bg-primary/10 text-primary"}`}>
                {c.tipo === "email" ? <Mail className="h-5 w-5" /> : c.tipo === "whatsapp" ? <MessageCircle className="h-5 w-5" /> : <Phone className="h-5 w-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{c.nome}</p>
                <p className="text-xs text-muted-foreground capitalize flex items-center gap-2">{c.tipo === "whatsapp" ? "WhatsApp" : c.tipo}{!c.attiva && <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300" data-testid={`campaign-scaduta-${c.id}`}>Scaduta</span>}</p>
              </div>
              <Button variant="ghost" size="icon" data-testid={`campaign-delete-${c.id}`} onClick={(e) => remove(e, c.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Megaphone className="h-3.5 w-3.5" />{c.lead_count} lead</span>
              {c.sede_name && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{c.sede_name}</span>}
              {c.collaboratori_names?.length > 0 && <span className="inline-flex items-center gap-1"><Users2 className="h-3.5 w-3.5" />{c.collaboratori_names.join(", ")}</span>}
            </div>
            {c.esito_counts && Object.keys(c.esito_counts).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`campaign-esiti-${c.id}`}>
                {Object.entries(c.esito_counts).sort((a, b) => b[1] - a[1]).map(([es, n]) => (
                  <span key={es} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">{es} <span className="font-semibold text-foreground tabular-nums">{n}</span></span>
                ))}
              </div>
            )}
          </Card>
        ))}
        {campaigns.length === 0 && <Card className="p-12 text-center text-muted-foreground col-span-full"><Megaphone className="h-8 w-8 mx-auto mb-2 opacity-40" />Nessuna campagna</Card>}
      </div>

      {user?.role === "admin" && (
        <div className="flex justify-start pt-2">
          <Button variant="outline" size="sm" data-testid="soppressioni-btn" onClick={() => navigate("/marketing/soppressioni")}>
            <ShieldOff className="h-4 w-4 mr-2" />Lista Soppressioni
          </Button>
        </div>
      )}
    </div>
  );
}
